import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

const minMax = require("dayjs/plugin/minMax");
const utc = require("dayjs/plugin/utc");

dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const dateText = ["日", "月", "火", "水", "木", "金", "土"];

export const formatDate = (
  date: string | Date,
  withDay?: boolean,
  format?: string,
) => {
  const formattedDate = dayjs(date).format(format ?? "YYYY/MM/DD");
  return withDay
    ? `${formattedDate}（${dateText[dayjs(date).day()]}）`
    : formattedDate;
};

export const formatDateTime = (
  dateTime: string | Date,
  withDay?: boolean,
  format?: string,
) => {
  const withDayFormat = `M/D (${dateText[dayjs(dateTime).day()]}) HH:mm`;

  return dayjs(dateTime).format(
    withDay ? withDayFormat : (format ?? "YYYY/MM/DD HH:mm:ss"),
  );
};

export const getCurrentDate = (format?: string) => {
  return formatDate(
    new Date(new Date().setHours(0, 0, 0, 0)),
    false,
    format ?? "YYYY-MM-DD",
  );
};

export const convertToISO8601 = (date: string | Date) => {
  // @ts-ignore
  return dayjs.utc(date).format("YYYY-MM-DDTHH:mm:ss.000[Z]");
};

export const getCurrentDateUTC = () => {
  return convertToISO8601(new Date());
};

export const getUTCDateByHourMinute = (
  date: string | Date,
  hour: number,
  minute: number,
) => {
  return convertToISO8601(
    dayjs(date).hour(hour).minute(minute).second(0).millisecond(0).toDate(),
  );
};

export const getUTCDatePlusMinutes = (date: string | Date, minutes: number) => {
  return convertToISO8601(dayjs(date).add(minutes, "minute").toDate());
};

export const getDatePlusMinutes = (date: string | Date, minutes: number) => {
  return dayjs(date).add(minutes, "minute").second(0).millisecond(0).toDate();
};

export const getDateMinusMinutes = (date: string | Date, minutes: number) => {
  return dayjs(date)
    .subtract(minutes, "minute")
    .second(0)
    .millisecond(0)
    .toDate();
};

export const getDatePlusDays = (date: string | Date, days: number) => {
  return dayjs(date).add(days, "day").toDate();
};

export const getDatesInRange = (
  startDate: string | Date,
  endDate: string | Date,
  format?: string,
): string[] => {
  const dateList = [];
  let currentDate = dayjs(startDate);

  // Iterate through the dates
  while (
    currentDate.isSame(dayjs(endDate)) ||
    currentDate.isBefore(dayjs(endDate))
  ) {
    const formattedDate = currentDate.format(format ?? "YYYY-MM-DD");
    dateList.push(formattedDate);

    // Move to the next day
    currentDate = currentDate.add(1, "day");
  }

  return dateList;
};

export const getMinAndMaxDate = (dates: Date[]) => {
  const dayjsDates = dates.map((date) => dayjs(date));

  // @ts-ignore
  const minDate: dayjs.Dayjs = dayjs.min(dayjsDates);
  // @ts-ignore
  const maxDate: dayjs.Dayjs = dayjs.max(dayjsDates);

  return [
    minDate && formatDate(minDate.toDate(), false, "YYYY-MM-DD"),
    maxDate && formatDate(maxDate.toDate(), false, "YYYY-MM-DD"),
  ];
};

export const isSameDate = (date1: string | Date, date2: string | Date) => {
  return dayjs(date1).isSame(date2, "day");
};

export const isFirstIsEarlierHourMinute = (
  hour1: number,
  minute1: number,
  hour2: number,
  minute2: number,
) => {
  const time1 = dayjs().hour(hour1).minute(minute1);
  const time2 = dayjs().hour(hour2).minute(minute2);
  return time1.isBefore(time2);
};

export const isDateTimeBefore = (
  date1: string | Date,
  date2: string | Date,
) => {
  return dayjs(date1).isBefore(date2);
};

export const isInPeriod = (
  startDate: string | Date,
  endDate: string | Date,
) => {
  const now = dayjs();
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return (
    (now.isAfter(start) && now.isBefore(end)) ||
    now.isSame(start) ||
    now.isSame(end)
  );
};

export const isDateTimeAfter = (date1: string | Date, date2: string | Date) => {
  return dayjs(date1).isAfter(date2);
};

export const isWithinMaintenancePeriod = (
  startDate?: string,
  endDate?: string,
): boolean => {
  if (!startDate || !endDate) return false;
  const now = dayjs();
  const start = dayjs(startDate, "YYYY-M-D+h:mm");
  const end = dayjs(endDate, "YYYY-M-D+h:mm");

  return now.diff(start) >= 0 && end.diff(now) >= 0;
};
